import { useNavigate } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Logo } from "../components/Logo";

export function TermsPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F6F4] flex flex-col">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 flex-shrink-0">
            <Logo className="w-10" />
            <span className="hidden sm:block text-sm font-semibold text-[#1F2933]">Leo Group</span>
          </button>
          <div className="flex-1" />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F2933] border border-[#E5E7EB] hover:border-[#D1D5DB] px-3 py-1.5 rounded transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-8 py-12 sm:py-16">

        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-2">Legal</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2933] mb-3">Terms &amp; Conditions</h1>
          <p className="text-sm text-[#6B7280]">Last updated: 1 May 2026 &nbsp;·&nbsp; Governed by the laws of the Republic of South Africa</p>
        </div>

        <div className="prose prose-sm max-w-none space-y-8 text-[#374151]">

          <Section title="1. Acceptance of Terms">
            <p>
              By accessing or using the LEO Group digital platform ("Platform"), website, or any services offered by
              <strong> LEO Group Holding Company (Pty) Ltd</strong> ("LEO Group", "we", "us", or "our"), you agree to be
              bound by these Terms &amp; Conditions ("Terms"). If you do not agree to these Terms, you must immediately
              cease use of the Platform.
            </p>
            <p>
              These Terms constitute a binding agreement in accordance with <strong>Section 11 of the Electronic
              Communications and Transactions Act 25 of 2002 (ECT Act)</strong>. By proceeding, you confirm that you have
              the legal capacity to enter into a binding agreement under South African law.
            </p>
          </Section>

          <Section title="2. Definitions">
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>"Platform"</strong> means the LEO Group website and secure stakeholder portal.</li>
              <li><strong>"User"</strong> means any person accessing the Platform, whether as a visitor or an approved stakeholder.</li>
              <li><strong>"Confidential Information"</strong> means all non-public business, technical, financial or operational information made available through the secure portal.</li>
              <li><strong>"NDA"</strong> means the Non-Disclosure Agreement executed upon approval of stakeholder access.</li>
              <li><strong>"South African law"</strong> means the law of the Republic of South Africa.</li>
            </ul>
          </Section>

          <Section title="3. Use of the Platform">
            <p>You agree to use the Platform only for lawful purposes and in a manner consistent with all applicable South African legislation, including but not limited to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The Electronic Communications and Transactions Act 25 of 2002</li>
              <li>The Cybercrimes Act 19 of 2020</li>
              <li>The Protection of Personal Information Act 4 of 2013 (POPIA)</li>
              <li>The Consumer Protection Act 68 of 2008</li>
            </ul>
            <p className="mt-3">You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Attempt to gain unauthorised access to any part of the Platform or its infrastructure;</li>
              <li>Reverse engineer, decompile, or attempt to extract source code from the Platform;</li>
              <li>Use automated tools, bots, or scrapers to access or index content on the Platform;</li>
              <li>Share, reproduce, or distribute any Confidential Information obtained through the secure portal;</li>
              <li>Use the Platform for any unlawful, fraudulent, or malicious purpose.</li>
            </ul>
          </Section>

          <Section title="4. Secure Access Portal">
            <p>
              Certain sections of the Platform are restricted to approved stakeholders. Access is granted solely via a
              unique, non-transferable access token issued by LEO Group following execution of an NDA. Approved stakeholders
              agree to:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Maintain the confidentiality of their access token;</li>
              <li>Not share, forward, or otherwise disclose access credentials to any third party;</li>
              <li>Immediately notify LEO Group if they suspect unauthorised use of their token;</li>
              <li>Comply with the terms of the NDA at all times.</li>
            </ul>
            <p className="mt-3">
              LEO Group reserves the right to revoke access at any time, with or without notice, at its sole discretion.
            </p>
          </Section>

          <Section title="5. Intellectual Property">
            <p>
              All content on the Platform, including but not limited to text, graphics, logos, software, documents,
              and proprietary platform descriptions, is the intellectual property of LEO Group or its licensors and is
              protected under the <strong>Copyright Act 98 of 1978</strong> and applicable intellectual property law.
            </p>
            <p className="mt-3">
              No content may be copied, reproduced, distributed, published, displayed, or otherwise exploited without
              the prior written consent of LEO Group. Any unauthorised use constitutes copyright infringement and may
              give rise to civil and criminal liability.
            </p>
          </Section>

          <Section title="6. Disclaimer of Warranties">
            <p>
              The Platform and all information contained therein are provided on an <strong>"as is" and "as available"</strong> basis
              without any warranty of any kind, whether express, implied, or statutory. LEO Group does not warrant that:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The Platform will be uninterrupted, error-free, or free of viruses or other harmful components;</li>
              <li>Any information presented on the Platform is accurate, complete, current, or reliable;</li>
              <li>The Platform will meet your specific requirements or expectations.</li>
            </ul>
            <p className="mt-3">
              To the maximum extent permitted by South African law, LEO Group disclaims all implied warranties including
              those of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
          </Section>

          <Section title="7. Limitation of Liability">
            <p>
              To the fullest extent permitted by the <strong>Consumer Protection Act 68 of 2008</strong> and other applicable
              South African legislation, LEO Group shall not be liable for any indirect, incidental, special, consequential,
              or punitive damages arising from your use of, or inability to use, the Platform.
            </p>
            <p className="mt-3">
              Where liability cannot be excluded by law, LEO Group's total aggregate liability shall not exceed the amount
              paid (if any) by you to LEO Group in the twelve (12) months preceding the event giving rise to the claim.
            </p>
          </Section>

          <Section title="8. Third-Party Links and Services">
            <p>
              The Platform may contain links to third-party websites or services. LEO Group has no control over the
              content, privacy practices, or availability of such third-party resources. The inclusion of any link does
              not imply endorsement by LEO Group. You access third-party resources at your own risk.
            </p>
          </Section>

          <Section title="9. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless LEO Group, its directors, officers, employees, agents,
              and licensors from and against any claims, damages, losses, costs, and expenses (including reasonable legal
              fees) arising out of or in connection with: (a) your use of the Platform; (b) your breach of these Terms;
              (c) your violation of any applicable law or regulation; or (d) your infringement of any third-party right.
            </p>
          </Section>

          <Section title="10. Amendments">
            <p>
              LEO Group reserves the right to amend these Terms at any time. Material changes will be notified by
              updating the "Last updated" date above. Continued use of the Platform following any amendment constitutes
              acceptance of the revised Terms. We encourage you to review these Terms periodically.
            </p>
          </Section>

          <Section title="11. Governing Law and Jurisdiction">
            <p>
              These Terms are governed by and construed in accordance with the laws of the <strong>Republic of South Africa</strong>.
              Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction
              of the courts of the Republic of South Africa.
            </p>
            <p className="mt-3">
              Before instituting legal proceedings, the parties agree to attempt to resolve disputes through good-faith
              negotiation for a period of 30 (thirty) days.
            </p>
          </Section>

          <Section title="12. Severability">
            <p>
              If any provision of these Terms is found to be unlawful, void, or unenforceable under South African law,
              that provision shall be severed from the remaining Terms, which shall continue in full force and effect.
            </p>
          </Section>

          <Section title="13. Entire Agreement">
            <p>
              These Terms, together with the Privacy Policy, POPIA Compliance Statement, and any applicable NDA,
              constitute the entire agreement between you and LEO Group with respect to the Platform and supersede all
              prior agreements, representations, and understandings.
            </p>
          </Section>

          <Section title="14. Contact">
            <p>For any queries regarding these Terms, please contact:</p>
            <address className="not-italic mt-2 text-sm space-y-1">
              <p><strong>LEO Group Holding Company (Pty) Ltd</strong></p>
              <p>Republic of South Africa</p>
              <p>Email: <a href="mailto:legal@leo-group.co.za" className="text-[#7B1A1A] hover:underline">legal@leo-group.co.za</a></p>
            </address>
          </Section>

        </div>
      </main>

      <Footer />
    </div>
  );
}

// ─── Shared sub-components ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-[#1F2933] mb-3 pb-2 border-b border-[#E5E7EB]">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="bg-[#6b1515] mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo className="w-10" white />
            <span className="text-sm font-medium text-white">Leo Group</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/60">
            <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy Policy</button>
            <button onClick={() => navigate("/compliance")} className="hover:text-white transition-colors">POPIA</button>
          </div>
        </div>
        <div className="border-t border-white/10 mt-6 pt-5 text-center text-xs text-white/30">
          © 2026 LEO Group Holding Company. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
